/**
 * Pedagogical Guard — Runtime question validation before rendering.
 *
 * PEDAGOGICAL_CONTRACT_SPEC §5 — Runs between adaptive engine output
 * and PracticeSession render. Rejects questions that violate the
 * pedagogical contract, replacing them with alternatives when possible.
 *
 * Design principles:
 * - Transparent to the learner (rejected questions are silently replaced)
 * - No flow interruption (session always has the expected question count)
 * - Telemetry logging for governance review
 * - Fail-safe: if no replacement found, soft-fail questions still pass through
 *
 * ─── PHASE 1b NOTE (Strak-Trek Refactor) ─────────────────────────────────────
 * The three checks below now delegate to the shared pure-check module at
 * `src/governance/checks/pedagogicalChecks.ts`. Both this runtime guard and
 * the build-time pipeline (`pedagogyStage.ts`) consume the same source of
 * truth, so the two layers can no longer disagree on:
 *   - Tier-DOK floor enforcement
 *   - Indicator-answer parity (string + numeric)
 *   - MC distractor misconception tagging at tier ≥ 9
 *
 * Telemetry-stable reason strings ('tier-dok-mismatch', 'indicator-trivial',
 * 'distractors-untagged') are preserved via the ISSUE_CODE_TO_GUARD_REASON
 * map below — `guardGovernanceBridge.ts` and any external log consumers
 * keep seeing the same machine-readable values they always did.
 *
 * Bug fixed as a side effect: the guard's indicator check previously only
 * did numeric-proximity matching; the pipeline always also did exact
 * string-match. Since both sides now share the same check, the guard now
 * catches exact-string indicator leaks too.
 */

import type { Question } from '../../content'
import { debug, warn } from '../../utils/logger'
import {
  checkDokFloor,
  checkIndicatorParity,
  checkDistractorsUntagged,
  type CheckResult,
} from '../../governance/checks/pedagogicalChecks'
import {
  ISSUE_CODES,
  type IssueCode,
} from '../../governance/pipeline/issues/codes'

// ─── Guard Verdict ────────────────────────────────────────────────────────────

export interface GuardVerdict {
  /** Whether the question passes the pedagogical guard */
  ok: boolean
  /** Machine-readable reason for rejection */
  reason?: string
  /** Severity: fatal = must replace, soft = allow but flag */
  severity?: 'fatal' | 'soft'
}

// ─── IssueCode → legacy guard-reason string ──────────────────────────────────
//
// Telemetry consumers (guardGovernanceBridge.ts, dashboards, logs) read these
// short reason strings — they MUST remain stable across the Phase 1b refactor.
// The shared check module produces ISSUE_CODES values; this map adapts them
// to the strings the runtime guard has always emitted.

const ISSUE_CODE_TO_GUARD_REASON: Partial<Record<IssueCode, string>> = {
  [ISSUE_CODES.PEDAGOGY_DOK_TOO_LOW]: 'tier-dok-mismatch',
  [ISSUE_CODES.PEDAGOGY_INDICATOR_LEAKS_ANSWER]: 'indicator-trivial',
  [ISSUE_CODES.PEDAGOGY_DISTRACTORS_UNTAGGED]: 'distractors-untagged',
}

function checkResultToVerdict(result: CheckResult): GuardVerdict {
  if (result.passed) return { ok: true }
  const reason =
    (result.code && ISSUE_CODE_TO_GUARD_REASON[result.code]) ?? 'unknown'
  return {
    ok: false,
    reason,
    severity: result.isFatal ? 'fatal' : 'soft',
  }
}

// ─── Core Evaluation ──────────────────────────────────────────────────────────

/**
 * Evaluate a single question against the pedagogical contract.
 *
 * Checks (delegated to `governance/checks/pedagogicalChecks.ts`):
 * 1. Tier-DOK floor (PEDAGOGY_DOK_TOO_LOW)      → 'tier-dok-mismatch'   (fatal)
 * 2. Indicator-answer parity (PEDAGOGY_INDICATOR_LEAKS_ANSWER) → 'indicator-trivial' (fatal)
 * 3. MC distractors untagged tier ≥ 9 (PEDAGOGY_DISTRACTORS_UNTAGGED) → 'distractors-untagged' (soft)
 *
 * First-fatal-wins: as soon as a fatal check fails we return; we still run
 * the soft check only after all fatal checks pass, mirroring the legacy
 * sequential behavior.
 */
export function evaluateQuestion(q: Question): GuardVerdict {
  // 1. Tier-DOK floor (fatal)
  const dokResult = checkDokFloor(q)
  if (!dokResult.passed) return checkResultToVerdict(dokResult)

  // 2. Indicator parity (fatal)
  const indicatorResult = checkIndicatorParity(q)
  if (!indicatorResult.passed) return checkResultToVerdict(indicatorResult)

  // 3. Distractors untagged (soft)
  const distractorsResult = checkDistractorsUntagged(q)
  if (!distractorsResult.passed) return checkResultToVerdict(distractorsResult)

  return { ok: true }
}

// ─── Session Filter ───────────────────────────────────────────────────────────

export interface GuardRejection {
  /** ID of the rejected question */
  questionId: string
  /** Family the question belongs to */
  familyId?: string
  /** Machine-readable rejection reason */
  reason: string
  /** Severity: fatal = replaced/removed, soft = allowed through */
  severity: 'fatal' | 'soft'
}

export interface GuardStats {
  total: number
  passed: number
  softFailed: number
  fatalRejected: number
  replaced: number
  reasons: Record<string, number>
  /** P-9: Per-question rejection details for governance feedback */
  rejections: GuardRejection[]
}

/**
 * Filter a session through the pedagogical guard.
 *
 * - Fatal rejections are removed (replaced if possible)
 * - Soft failures pass through but are logged
 * - Returns filtered questions and stats for telemetry
 *
 * @param questions - Raw session from adaptive engine
 * @param targetCount - Desired session length
 * @param replacementPool - Optional pool of replacement questions (same module)
 */
export function filterSession(
  questions: Question[],
  targetCount: number,
  replacementPool?: Question[],
): { questions: Question[]; stats: GuardStats } {
  const stats: GuardStats = {
    total: questions.length,
    passed: 0,
    softFailed: 0,
    fatalRejected: 0,
    replaced: 0,
    reasons: {},
    rejections: [],
  }

  const filtered: Question[] = []
  const usedIds = new Set(questions.map((q) => q.id))

  for (const q of questions) {
    const verdict = evaluateQuestion(q)

    if (verdict.ok) {
      filtered.push(q)
      stats.passed++
      continue
    }

    // Track reason
    const reason = verdict.reason ?? 'unknown'
    stats.reasons[reason] = (stats.reasons[reason] ?? 0) + 1

    if (verdict.severity === 'soft') {
      // Soft fail: allow through but log
      filtered.push(q)
      stats.softFailed++
      stats.rejections.push({
        questionId: q.id,
        familyId: q.familyId,
        reason,
        severity: 'soft',
      })
      debug(
        `[PedagogicalGuard] Soft fail: ${q.id} — ${reason} (allowed through)`,
      )
      continue
    }

    // Fatal: try to find replacement
    stats.fatalRejected++
    stats.rejections.push({
      questionId: q.id,
      familyId: q.familyId,
      reason,
      severity: 'fatal',
    })
    warn(`[PedagogicalGuard] Fatal reject: ${q.id} — ${reason}`)

    if (replacementPool) {
      const replacement = findReplacement(q, replacementPool, usedIds)
      if (replacement) {
        filtered.push(replacement)
        usedIds.add(replacement.id)
        stats.replaced++
        debug(`[PedagogicalGuard] Replaced ${q.id} with ${replacement.id}`)
      }
    }
  }

  // Trim to target count
  const result = filtered.slice(0, targetCount)

  return { questions: result, stats }
}

/**
 * Find a replacement question from the same family and similar tier.
 */
function findReplacement(
  rejected: Question,
  pool: Question[],
  usedIds: Set<string>,
): Question | null {
  // Prefer same family, same tier
  const sameFamilySameTier = pool.find(
    (q) =>
      q.familyId === rejected.familyId &&
      q.tier === rejected.tier &&
      !usedIds.has(q.id) &&
      evaluateQuestion(q).ok,
  )
  if (sameFamilySameTier) return sameFamilySameTier

  // Same family, adjacent tier (±1)
  const sameFamilyNearTier = pool.find(
    (q) =>
      q.familyId === rejected.familyId &&
      Math.abs(q.tier - rejected.tier) <= 1 &&
      !usedIds.has(q.id) &&
      evaluateQuestion(q).ok,
  )
  if (sameFamilyNearTier) return sameFamilyNearTier

  // Same module, same tier
  const sameModuleSameTier = pool.find(
    (q) =>
      q.module === rejected.module &&
      q.tier === rejected.tier &&
      !usedIds.has(q.id) &&
      evaluateQuestion(q).ok,
  )
  if (sameModuleSameTier) return sameModuleSameTier

  return null
}
